/**
 * ExpenseForm Component
 * Shared form for creating and editing expenses
 */

import { useState, useEffect, useRef } from 'react';
import type { Expense, ExpenseCategory, CreateExpenseBody, UpdateExpenseBody } from '../../types/expense.types';
import { centsToDollarString, parseDollarsToCents } from '../../utils/expense.utils';
import { todayInTimezone, realUnitNumber } from '@resida/shared';
import { listProperties, listUnits } from '../../lib/api';
import type { PropertyWithStats, UnitWithRelations } from '../../lib/api';

interface ExpenseFormProps {
  mode: 'create' | 'edit';
  propertyId: string;
  unitId?: string;
  initialValues?: Partial<Expense>;
  categories: ExpenseCategory[];
  onSubmit: (values: CreateExpenseBody | UpdateExpenseBody) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  /** IANA timezone string for the property. Defaults to America/New_York if not provided. */
  timezone?: string;
}

export function ExpenseForm({
  mode,
  propertyId,
  unitId,
  initialValues,
  categories,
  onSubmit,
  onCancel,
  isLoading = false,
  timezone = 'America/New_York',
}: ExpenseFormProps) {
  const [amountDisplay, setAmountDisplay] = useState<string>(
    initialValues?.amount != null ? centsToDollarString(initialValues.amount) : ''
  );
  const [expenseDate, setExpenseDate] = useState<string>(
    initialValues?.expenseDate ?? todayInTimezone(timezone)
  );
  const [description, setDescription] = useState<string>(
    initialValues?.description ?? ''
  );
  const [vendorName, setVendorName] = useState<string>(
    initialValues?.vendorName ?? ''
  );
  const [irsCategoryId, setIrsCategoryId] = useState<string>(
    initialValues?.irsCategoryId ?? ''
  );
  const [notes, setNotes] = useState<string>(
    initialValues?.notes ?? ''
  );
  const [error, setError] = useState<string | null>(null);

  // Property/unit correction — edit mode only (AC-1, AC-2, AC-3)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(initialValues?.propertyId ?? '');
  const [selectedUnitId, setSelectedUnitId] = useState<string>(initialValues?.unitId ?? '');
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [propertiesLoadError, setPropertiesLoadError] = useState(false);
  const [unitsLoadError, setUnitsLoadError] = useState(false);
  const [propertiesReloadToken, setPropertiesReloadToken] = useState(0);
  const [unitsReloadToken, setUnitsReloadToken] = useState(0);
  // Tracks the property id the unit selector's options were last populated for, so the
  // property-change cascade (below) can distinguish a user-driven property change from the
  // initial mount / an initialValues resync — neither of which should wipe the pre-filled unit.
  const previousPropertyIdRef = useRef<string>(initialValues?.propertyId ?? '');

  // Update form when initialValues change (e.g. switching between expenses)
  useEffect(() => {
    if (initialValues) {
      setAmountDisplay(initialValues.amount != null ? centsToDollarString(initialValues.amount) : '');
      setExpenseDate(initialValues.expenseDate ?? todayInTimezone(timezone));
      setDescription(initialValues.description ?? '');
      setVendorName(initialValues.vendorName ?? '');
      setIrsCategoryId(initialValues.irsCategoryId ?? '');
      setNotes(initialValues.notes ?? '');
      setSelectedPropertyId(initialValues.propertyId ?? '');
      setSelectedUnitId(initialValues.unitId ?? '');
      previousPropertyIdRef.current = initialValues.propertyId ?? '';
    }
  }, [initialValues, timezone]);

  // Load the landlord's properties for the property selector — edit mode only.
  useEffect(() => {
    if (mode !== 'edit') return;
    setPropertiesLoading(true);
    setPropertiesLoadError(false);
    listProperties()
      .then((res) => setProperties(res.properties))
      .catch(() => setPropertiesLoadError(true))
      .finally(() => setPropertiesLoading(false));
  }, [mode, propertiesReloadToken]);

  // Cascade: re-populate unit options when the selected property changes, and reset the unit
  // selection when the property actually changed (AC-3) — but not on initial mount / resync.
  useEffect(() => {
    if (mode !== 'edit') return;
    if (!selectedPropertyId) {
      setUnits([]);
      setUnitsLoadError(false);
      previousPropertyIdRef.current = selectedPropertyId;
      return;
    }
    setUnitsLoading(true);
    setUnitsLoadError(false);
    listUnits({ propertyId: selectedPropertyId })
      .then((res) => setUnits(res.units))
      .catch(() => setUnitsLoadError(true))
      .finally(() => setUnitsLoading(false));

    if (previousPropertyIdRef.current !== selectedPropertyId) {
      setSelectedUnitId('');
    }
    previousPropertyIdRef.current = selectedPropertyId;
  }, [mode, selectedPropertyId, unitsReloadToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const amountCents = parseDollarsToCents(amountDisplay);
    if (amountCents < 0) {
      setError('Amount must be zero or positive.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (!expenseDate) {
      setError('Date is required.');
      return;
    }
    if (mode === 'edit' && !selectedPropertyId) {
      setError('Property is required.');
      return;
    }

    try {
      if (mode === 'create') {
        const body: CreateExpenseBody = {
          scope: unitId ? 'UNIT' : 'PROPERTY',
          propertyId,
          unitId: unitId || undefined,
          amount: amountCents,
          expenseDate,
          description: description.trim(),
          vendorName: vendorName.trim() || undefined,
          irsCategoryId: irsCategoryId || undefined,
          notes: notes.trim() || undefined,
        };
        await onSubmit(body);
      } else {
        const body: UpdateExpenseBody = {
          propertyId: selectedPropertyId,
          unitId: selectedUnitId || null,
          amount: amountCents,
          expenseDate,
          description: description.trim(),
          vendorName: vendorName.trim() || undefined,
          irsCategoryId: irsCategoryId || undefined,
          notes: notes.trim() || undefined,
        };
        await onSubmit(body);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save expense');
    }
  };

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="expense-form"
      aria-label={mode === 'create' ? 'Create expense' : 'Edit expense'}
    >
      {error && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* Amount */}
        <div>
          <label
            htmlFor="expense-form-amount"
            className="block text-xs font-medium text-foreground mb-1"
          >
            Amount *
          </label>
          <div className="relative">
            <span
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none"
              aria-hidden="true"
            >
              $
            </span>
            <input
              id="expense-form-amount"
              type="text"
              inputMode="decimal"
              value={amountDisplay}
              onChange={(e) => setAmountDisplay(e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-full pl-7 pr-3 py-2 text-sm border border-input rounded-md outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
              placeholder="0.00"
              required
              data-testid="expense-form-amount"
            />
          </div>
        </div>

        {/* Date */}
        <div>
          <label
            htmlFor="expense-form-date"
            className="block text-xs font-medium text-foreground mb-1"
          >
            Date *
          </label>
          <input
            id="expense-form-date"
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            required
            data-testid="expense-form-date"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* Description */}
        <div>
          <label
            htmlFor="expense-form-description"
            className="block text-xs font-medium text-foreground mb-1"
          >
            Description *
          </label>
          <input
            id="expense-form-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            placeholder="e.g. Roof repair, Plumbing inspection"
            required
            data-testid="expense-form-description"
          />
        </div>

        {/* Vendor */}
        <div>
          <label
            htmlFor="expense-form-vendor"
            className="block text-xs font-medium text-foreground mb-1"
          >
            Vendor Name
          </label>
          <input
            id="expense-form-vendor"
            type="text"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
            placeholder="e.g. ABC Plumbing"
            data-testid="expense-form-vendor"
          />
        </div>
      </div>

      {mode === 'edit' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {/* Property */}
          <div>
            <label
              htmlFor="expense-form-property"
              className="block text-xs font-medium text-foreground mb-1"
            >
              Property *
            </label>
            <select
              id="expense-form-property"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              required
              aria-required="true"
              disabled={propertiesLoading}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-card outline-none focus:ring-2 focus:ring-ring min-h-[44px] disabled:opacity-50"
              data-testid="expense-form-property-select"
            >
              <option value="">
                {propertiesLoading ? 'Loading properties…' : '-- Select Property --'}
              </option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {propertiesLoadError && (
              <p role="alert" className="text-xs text-destructive mt-1">
                Unable to load properties.{' '}
                <button
                  type="button"
                  onClick={() => setPropertiesReloadToken((n) => n + 1)}
                  className="underline"
                  data-testid="expense-form-property-retry"
                >
                  Retry
                </button>
              </p>
            )}
          </div>

          {/* Unit */}
          <div>
            <label
              htmlFor="expense-form-unit"
              className="block text-xs font-medium text-foreground mb-1"
            >
              Unit
            </label>
            <select
              id="expense-form-unit"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              disabled={!selectedPropertyId || unitsLoading}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-card outline-none focus:ring-2 focus:ring-ring min-h-[44px] disabled:opacity-50"
              data-testid="expense-form-unit-select"
            >
              <option value="">{unitsLoading ? 'Loading units…' : 'No specific unit'}</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {realUnitNumber(u.unitNumber) ?? u.address1}
                </option>
              ))}
            </select>
            {unitsLoadError && (
              <p role="alert" className="text-xs text-destructive mt-1">
                Unable to load units.{' '}
                <button
                  type="button"
                  onClick={() => setUnitsReloadToken((n) => n + 1)}
                  className="underline"
                  data-testid="expense-form-unit-retry"
                >
                  Retry
                </button>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Category — full width */}
      <div className="mb-4">
        <label
          htmlFor="expense-form-category"
          className="block text-xs font-medium text-foreground mb-1"
        >
          IRS Category
        </label>
        <select
          id="expense-form-category"
          value={irsCategoryId}
          onChange={(e) => setIrsCategoryId(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-card outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          data-testid="expense-form-category"
        >
          <option value="">-- Select category --</option>
          {sortedCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
              {cat.scheduleELine ? ` (Schedule E: ${cat.scheduleELine})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Notes — full width */}
      <div className="mb-4">
        <label
          htmlFor="expense-form-notes"
          className="block text-xs font-medium text-foreground mb-1"
        >
          Notes
        </label>
        <textarea
          id="expense-form-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-input rounded-md outline-none focus:ring-2 focus:ring-ring resize-y"
          style={{ minHeight: '80px', fontFamily: 'inherit' }}
          placeholder="Optional additional notes"
          data-testid="expense-form-notes"
        />
      </div>

      <div className="flex gap-3 justify-end mt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground underline disabled:opacity-50 min-h-[44px]"
          data-testid="expense-form-cancel"
          aria-label="Cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 min-h-[44px]"
          data-testid="expense-form-submit"
          aria-label={mode === 'create' ? 'Create expense' : 'Save changes'}
        >
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Expense' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

export default ExpenseForm;
